import type { Analytics, IdentifyOptions, PageOptions } from '@avalara/types/index';
import { getQueryParams } from '../components/helpers';
import conf from '../conf';
import fetch from './fetch';

const baseParams = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
};

// This file should load once per browser load/refresh. This state will prevent us
// from calling identify multiple times for a single user (until another refresh):
let seenUser = false;

interface BuyAnalytics extends Analytics {
  sendError: (body: Record<string, unknown>) => Promise<void>;
  sendMetric: (body: Record<string, unknown>) => Promise<void>;
}

const analytics: BuyAnalytics = {
  base: {
    accountId: 'default', // default to tech support user
    queryParams: getQueryParams(window.location.search),
  },

  sendError: async (body: Record<string, unknown>) => {
    await fetch(() => {}, { showLoadingWheel: false })(`${conf.API_URL}/clienterror`, {
      ...baseParams,
      body: JSON.stringify(body),
    });
  },

  sendMetric: async (body: Record<string, unknown>) => {
    await fetch(() => {}, { showLoadingWheel: false })(`${conf.API_URL}/api/clientmetric`, {
      ...baseParams,
      body: JSON.stringify(body),
    });
  },

  set<T>(key: string, value: T) {
    this.base[key] = value;
  },

  identify(userId: string, options: IdentifyOptions) {
    if (!seenUser) {
      seenUser = true;

      const { FS } = window;

      FS?.identify(userId, { ...options, id: userId });

      const body = { type: 'CLIENT_IDENTIFY', userId, url: window.location.href };

      this.sendMetric(body);
    }
  },

  page(page: string, options?: PageOptions) {
    const body = {
      type: 'CLIENT_PAGE_VIEW',
      ...this.base,
      page,
      url: window?.location?.href,
      ...options,
    };

    this.sendMetric(body);
  },

  track<T>(eventName: string, data?: T) {
    const body = {
      type: 'CLIENT_EVENT',
      ...this.base,
      eventName,
      url: window?.location?.href,
      ...data,
    };

    if (data != null && typeof data === 'object' && 'error' in data && data.error === true) {
      this.sendError(body);
    } else {
      this.sendMetric(body);
    }
  },
};

export default analytics;

export const scrubPostBody = <T extends Record<string, unknown>>(body: T) => {
  if (typeof body !== 'object' || body == null) return body;
  const b: Record<string, unknown> = { ...body };

  for (const key of Object.keys(body)) {
    if (b[key] instanceof Object) {
      b[key] = scrubPostBody(b[key] as Record<string, unknown>);
    }

    if (/email/gi.test(key)) {
      b[key] = '<email>';
    } else if (/password/gi.test(key)) {
      b[key] = '<password>';
    } else if (/key/gi.test(key)) {
      b[key] = '<key>';
    } else if (/paymentmethodid/gi.test(key)) {
      b[key] = '<paymentMethodId>';
    }
  }
  return b;
};
